import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiFilter, FiGrid, FiList, FiHeart, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import api from '../services/api';
import LazyImage from '../components/ui/LazyImage';
import { useWishlist } from '../contexts/WishlistContext';
import { trackProductClick } from '../utils/analytics';

const getProductId = (product) => product?.id || product?._id || null;

const Products = () => {
  const { isInWishlist, toggleWishlist } = useWishlist();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    collection_id: searchParams.get('collection_id') || '',
    category: searchParams.get('category') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    search: searchParams.get('search') || '',
    size: searchParams.get('size') || '',
    in_stock: searchParams.get('in_stock') === 'true',
    sort: 'newest'
  });
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    collection: true,
    price: true,
    size: true,
    availability: true
  });

  // Fetch collections for filter options
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await api.publicAPI.getCollections();
        if (response.data.success) {
          setCollections(response.data.collections);
        }
      } catch (err) {
        console.error('Error fetching collections:', err);
      }
    };

    fetchCollections();
  }, []);

  // Fetch products when filters change
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {};
        
        // Add filters to params
        Object.keys(filters).forEach(key => {
          if (filters[key] && filters[key] !== '') {
            params[key] = filters[key];
          }
        });

        const response = await api.publicAPI.getProducts({ ...params, list_only: 'true' });
        
        if (response.data.success) {
          let fetchedProducts = response.data.products;
          
          // Apply client-side sorting
          switch (filters.sort) {
            case 'price-low':
              fetchedProducts.sort((a, b) => a.price - b.price);
              break;
            case 'price-high':
              fetchedProducts.sort((a, b) => b.price - a.price);
              break;
            case 'popular':
              fetchedProducts.sort((a, b) => (b.total_stock || 0) - (a.total_stock || 0));
              break;
            case 'newest':
            default:
              // Already sorted by created_at in backend
              break;
          }

          // Apply client-side size and stock filters
          if (filters.size) {
            fetchedProducts = fetchedProducts.filter((product) => {
              const productSizes = product.available_sizes || product.sizes || [];
              return productSizes.includes(filters.size);
            });
          }

          if (filters.in_stock) {
            fetchedProducts = fetchedProducts.filter((product) => {
              const totalStock = product.totalStock ?? product.total_stock ?? 0;
              return totalStock > 0;
            });
          }
          
          setProducts(fetchedProducts);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(api.utils.handleError(err));
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [filters]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] && filters[key] !== '' && key !== 'sort') {
        params.set(key, filters[key]);
      }
    });
    setSearchParams(params);
  }, [filters, setSearchParams]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      collection_id: '',
      category: '',
      min_price: '',
      max_price: '',
      search: '',
      size: '',
      in_stock: false,
      sort: 'newest'
    });
  };

  const priceRanges = [
    { value: '', label: 'All Prices' },
    { min: '', max: '1000', label: 'Under ₹1,000' },
    { min: '1000', max: '2000', label: '₹1,000 - ₹2,000' },
    { min: '2000', max: '3000', label: '₹2,000 - ₹3,000' },
    { min: '3000', max: '5000', label: '₹3,000 - ₹5,000' },
    { min: '5000', max: '', label: 'Above ₹5,000' }
  ];

  const handlePriceRangeChange = (range) => {
    setFilters(prev => ({
      ...prev,
      min_price: range.min || '',
      max_price: range.max || ''
    }));
  };

  const availableSizes = Array.from(
    new Set(
      products.flatMap((product) => product.available_sizes || product.sizes || [])
    )
  );

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const handleWishlistClick = async (event, product) => {
    event.preventDefault();
    event.stopPropagation();
    await toggleWishlist(getProductId(product));
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Products</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Filters Sidebar */}
        <div className={`lg:w-64 ${showFilters ? 'block' : 'hidden lg:block'}`}>
          <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Filters</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-black"
              >
                Clear All
              </button>
            </div>

            {/* Collection Filter */}
            <div className="mb-6">
              <button
                type="button"
                onClick={() => toggleSection('collection')}
                className="w-full flex items-center justify-between font-medium mb-3"
              >
                <span>Collection</span>
                {expandedSections.collection ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
              </button>
              {expandedSections.collection && (
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="collection"
                      value=""
                      checked={filters.collection_id === ''}
                      onChange={(e) => handleFilterChange('collection_id', e.target.value)}
                      className="mr-2"
                    />
                    <span>All Collections</span>
                  </label>
                  {collections.map(collection => (
                    <label key={collection.id} className="flex items-center">
                      <input
                        type="radio"
                        name="collection"
                        value={collection.id}
                        checked={filters.collection_id === collection.id}
                        onChange={(e) => handleFilterChange('collection_id', e.target.value)}
                        className="mr-2"
                      />
                      <span>{collection.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Price Range Filter */}
            <div className="mb-6">
              <button
                type="button"
                onClick={() => toggleSection('price')}
                className="w-full flex items-center justify-between font-medium mb-3"
              >
                <span>Price Range</span>
                {expandedSections.price ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
              </button>
              {expandedSections.price && (
                <div className="space-y-2">
                  {priceRanges.map((range, index) => {
                    const isSelected = filters.min_price === (range.min || '') &&
                                     filters.max_price === (range.max || '');
                    return (
                      <label key={index} className="flex items-center">
                        <input
                          type="radio"
                          name="priceRange"
                          checked={isSelected}
                          onChange={() => handlePriceRangeChange(range)}
                          className="mr-2"
                        />
                        <span>{range.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Size Filter */}
            <div className="mb-6">
              <button
                type="button"
                onClick={() => toggleSection('size')}
                className="w-full flex items-center justify-between font-medium mb-3"
              >
                <span>Size</span>
                {expandedSections.size ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
              </button>
              {expandedSections.size && (
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="size"
                      value=""
                      checked={filters.size === ''}
                      onChange={(e) => handleFilterChange('size', e.target.value)}
                      className="mr-2"
                    />
                    <span>All Sizes</span>
                  </label>
                  {availableSizes.map((size) => (
                    <label key={size} className="flex items-center">
                      <input
                        type="radio"
                        name="size"
                        value={size}
                        checked={filters.size === size}
                        onChange={(e) => handleFilterChange('size', e.target.value)}
                        className="mr-2"
                      />
                      <span>{size}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Availability Filter */}
            <div className="mb-6">
              <button
                type="button"
                onClick={() => toggleSection('availability')}
                className="w-full flex items-center justify-between font-medium mb-3"
              >
                <span>Availability</span>
                {expandedSections.availability ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
              </button>
              {expandedSections.availability && (
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.in_stock}
                      onChange={(e) => handleFilterChange('in_stock', e.target.checked)}
                      className="mr-2"
                    />
                    <span>In Stock Only</span>
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="flex-1">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold mb-2">
                {filters.collection_id ? 
                  collections.find(c => c.id === filters.collection_id)?.name || 'Products' :
                  filters.category ? 
                    filters.category.replace('-', ' ').toUpperCase() : 
                    'All Products'
                }
              </h1>
              <p className="text-gray-600">
                {loading ? 'Loading...' : `${products.length} products found`}
              </p>
            </div>

            <div className="flex items-center space-x-4 mt-4 sm:mt-0">
              {/* Mobile Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden btn-secondary flex items-center"
              >
                <FiFilter className="mr-2" size={16} />
                Filters
              </button>

              {/* View Mode Toggle */}
              <div className="flex border rounded-lg">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-black text-white' : 'hover:bg-gray-100'}`}
                >
                  <FiGrid size={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-black text-white' : 'hover:bg-gray-100'}`}
                >
                  <FiList size={16} />
                </button>
              </div>

              {/* Sort */}
              <select
                value={filters.sort}
                onChange={(e) => handleFilterChange('sort', e.target.value)}
                className="input-field min-w-0"
              >
                <option value="newest">Newest First</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className={`${viewMode === 'grid' ? 'product-grid' : 'grid grid-cols-1 gap-6'}`}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className={`card animate-pulse ${viewMode === 'list' ? 'flex' : ''}`}>
                  <div className={`bg-gray-200 ${viewMode === 'list' ? 'w-48 aspect-[4/5]' : 'aspect-[4/5]'} rounded-t-xl`}></div>
                  <div className="p-6 flex-1">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : products.length > 0 ? (
            <>
              {/* Products Grid */}
              <div className={`${viewMode === 'grid' ? 'product-grid' : 'grid grid-cols-1 gap-6'}`}>
                {products.map((product, index) => (
                  <Link
                    key={getProductId(product) || `product-${index}`}
                    to={getProductId(product) ? `/products/${getProductId(product)}` : '/products'}
                    onClick={() => trackProductClick(product)}
                    className={`card group ${viewMode === 'list' ? 'flex' : ''}`}
                  >
                    <div className={`relative overflow-hidden bg-white ${viewMode === 'list' ? 'w-48 aspect-[4/5] flex-shrink-0' : 'aspect-[4/5]'}`}>
                      <LazyImage
                        src={product.images?.[0]}
                        alt={product.name}
                        width={600}
                        height={750}
                        fit="cover"
                        className="group-hover:scale-105 transition-transform duration-300"
                      />
                      {product.badge && (
                        <span className="absolute top-4 left-4 bg-black text-white text-xs px-2 py-1 rounded">
                          {product.badge}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={(event) => handleWishlistClick(event, product)}
                        className={`absolute top-4 right-4 p-2 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity ${
                          isInWishlist(getProductId(product)) ? 'text-red-600 opacity-100' : 'text-gray-700'
                        }`}
                        aria-label="Add to wishlist"
                      >
                        <FiHeart
                          size={16}
                          fill={isInWishlist(getProductId(product)) ? 'currentColor' : 'none'}
                        />
                      </button>
                      
                      {/* Stock Overlay */}
                      {!api.utils.isProductAvailable(product) && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                          <span className="text-white font-semibold">Out of Stock</span>
                        </div>
                      )}
                    </div>
                    <div className={`product-card-info ${viewMode === 'list' ? 'flex-1' : ''}`}>
                      <h3 className="text-base lg:text-sm font-semibold mb-2 group-hover:text-gray-600 transition-colors line-clamp-2">
                        {product.name}
                      </h3>
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="text-lg lg:text-base font-bold text-black">
                          ₹{product.price.toLocaleString()}
                        </span>
                        {product.original_price && product.original_price > product.price && (
                          <span className="text-sm text-gray-500 line-through">
                            ₹{product.original_price.toLocaleString()}
                          </span>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        {/* Collection */}
                        {product.collection_name && (
                          <p className="text-sm text-gray-600">{product.collection_name}</p>
                        )}
                        
                        {/* Stock Status */}
                        <div className="flex items-center justify-between">
                                                  <div className="text-sm">
                          {product.totalStock > 0 ? (
                            <span className={`${product.totalStock <= 10 ? 'text-yellow-600' : 'text-green-600'}`}>
                              {product.totalStock <= 10 ? `Only ${product.totalStock} left` : 'In Stock'}
                            </span>
                          ) : (
                            <span className="text-red-600">Out of Stock</span>
                          )}
                        </div>
                          
                          {/* Available Sizes */}
                          <div className="flex space-x-1">
                            {product.available_sizes?.slice(0, 3).map((size, index) => (
                              <span
                                key={index}
                                className="text-xs bg-gray-100 px-1 py-0.5 rounded"
                              >
                                {size}
                              </span>
                            ))}
                            {product.available_sizes?.length > 3 && (
                              <span className="text-xs text-gray-500">
                                +{product.available_sizes.length - 3}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Colors Available */}
                        {product.colors && product.colors.length > 0 && (
                          <div className="flex items-center space-x-1">
                            {product.colors.slice(0, 3).map((color, index) => (
                              <div
                                key={index}
                                className={`w-4 h-4 rounded-full border border-gray-300 ${
                                  color === 'Black' ? 'bg-black' :
                                  color === 'White' ? 'bg-white' :
                                  color === 'Navy' ? 'bg-blue-900' :
                                  color === 'Blue' ? 'bg-blue-500' :
                                  color === 'Grey' ? 'bg-gray-500' :
                                  color === 'Brown' ? 'bg-yellow-800' :
                                  'bg-gray-300'
                                }`}
                                title={color}
                              />
                            ))}
                            {product.colors.length > 3 && (
                              <span className="text-xs text-gray-500">+{product.colors.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-16">
              <h3 className="text-xl font-semibold mb-4">No Products Found</h3>
              <p className="text-gray-600 mb-8">
                Try adjusting your filters
              </p>
              <button onClick={clearFilters} className="btn-primary">
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Products; 